import React, { useState, useEffect } from 'react';
import { Users, Plus, XCircle, CheckCircle, Activity, Building2, UserCheck, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4002';

// Helper to get admin auth token
const getAdminToken = () => {
  const authState = localStorage.getItem('bpv_admin_auth_state');
  if (!authState) throw new Error('Please login again');
  const { session } = JSON.parse(authState);
  if (!session?.access_token) throw new Error('Please login again');
  return session.access_token;
};

const WarehouseManagersPage = () => {
  const [assignments, setAssignments] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [activeTab, setActiveTab] = useState('assignments'); // assignments, activity
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    warehouse_id: '',
    staff_id: '',
    is_primary_manager: false,
    started_at: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchWarehouses();
    fetchAvailableStaff();
    fetchAssignments();
  }, []);

  useEffect(() => {
    fetchAssignments();
    if (activeTab === 'activity') {
      fetchAllActivities();
    }
  }, [selectedWarehouse, activeTab]);

  const fetchWarehouses = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_URL}/api/admin/warehouses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setWarehouses(result.warehouses || []);
      }
    } catch (err) {
      console.error('Error fetching warehouses:', err);
    }
  };

  const fetchAvailableStaff = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_URL}/api/admin/warehouse-managers/available-staff`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setAvailableStaff(result.staff || []);
      } else {
        console.error('Failed to fetch staff:', result.message);
      }
    } catch (err) {
      console.error('Error fetching staff:', err);
    }
  };

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const token = getAdminToken();
      
      let url = `${API_URL}/api/admin/warehouse-managers`;
      if (selectedWarehouse !== 'all') {
        url += `?warehouse_id=${selectedWarehouse}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      if (result.success) {
        setAssignments(result.assignments || []);
      }
    } catch (err) {
      console.error('Error fetching assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllActivities = async () => {
    try {
      setActivityLoading(true);
      const token = getAdminToken();
      
      // Fetch activities for all assignments or filtered by warehouse
      const filteredAssignments = selectedWarehouse !== 'all' 
        ? assignments.filter(a => a.warehouse_id === selectedWarehouse)
        : assignments;

      if (filteredAssignments.length === 0) {
        setActivities([]);
        setActivityLoading(false);
        return;
      }

      // Fetch activities for each assignment
      const activityPromises = filteredAssignments.map(async (assignment) => {
        try {
          const response = await fetch(
            `${API_URL}/api/admin/warehouse-managers/${assignment.assignment_id}/activity?limit=20`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          const result = await response.json();
          if (result.success) {
            return (result.activities || []).map(act => ({
              ...act,
              staff_name: assignment.staff_name,
              warehouse_name: assignment.warehouse_name,
              warehouse_code: assignment.warehouse_code
            }));
          }
          return [];
        } catch (err) {
          console.error('Error fetching activity for assignment:', assignment.assignment_id, err);
          return [];
        }
      });

      const allActivities = await Promise.all(activityPromises);
      const flatActivities = allActivities.flat();
      
      // Sort by performed_at descending
      flatActivities.sort((a, b) => new Date(b.performed_at) - new Date(a.performed_at));
      
      setActivities(flatActivities);
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setActivityLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.warehouse_id) newErrors.warehouse_id = 'Warehouse is required';
    if (!formData.staff_id) newErrors.staff_id = 'Staff member is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_URL}/api/admin/warehouse-managers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSuccessMessage(result.message || 'Staff assigned successfully');
        setShowForm(false);
        resetForm();
        fetchAssignments();
        
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.message || 'Failed to assign staff' });
      }
    } catch (err) {
      setErrors({ general: 'Error assigning staff: ' + err.message });
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this staff assignment? They will no longer have access to this warehouse.')) {
      return;
    }
    
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_URL}/api/admin/warehouse-managers/${assignmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSuccessMessage(result.message || 'Assignment removed successfully');
        fetchAssignments();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        alert('Failed to remove assignment: ' + result.message);
      }
    } catch (err) {
      alert('Error removing assignment: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      warehouse_id: '',
      staff_id: '',
      is_primary_manager: false,
      started_at: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="text-blue-600" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Warehouse Staff Management</h1>
              <p className="text-sm text-gray-600">Assign staff members to warehouses</p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Assign Staff to Warehouse
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
          <CheckCircle size={20} />
          {successMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('assignments')}
            className={`pb-3 px-2 border-b-2 font-medium transition-colors ${
              activeTab === 'assignments'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Staff Assignments
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`pb-3 px-2 border-b-2 font-medium transition-colors ${
              activeTab === 'activity'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Activity Log
          </button>
        </nav>
      </div>

      {/* Assignment Form */}
      {showForm && (
        <div className="mb-6 bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Assign Staff to Warehouse</h2>
          
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {errors.general}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            {/* Warehouse Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warehouse *
              </label>
              <select
                name="warehouse_id"
                value={formData.warehouse_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.warehouse_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Warehouse</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>
                    {w.code} - {w.name} ({w.city})
                  </option>
                ))}
              </select>
              {errors.warehouse_id && (
                <p className="mt-1 text-xs text-red-600">{errors.warehouse_id}</p>
              )}
            </div>

            {/* Staff Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Staff Member *
              </label>
              <select
                name="staff_id"
                value={formData.staff_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.staff_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Staff</option>
                {availableStaff.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.employee_id} - {s.full_name} ({s.login_email})
                  </option>
                ))}
              </select>
              {errors.staff_id && (
                <p className="mt-1 text-xs text-red-600">{errors.staff_id}</p>
              )}
              {availableStaff.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  No staff with Warehouse Manager role found. Create staff first.
                </p>
              )}
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="started_at"
                value={formData.started_at}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Primary Manager Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_primary_manager"
                checked={formData.is_primary_manager}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Primary Manager for this warehouse
              </label>
            </div>

            {/* Notes */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional information about this assignment..."
              />
            </div>

            {/* Buttons */}
            <div className="col-span-2 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Assign Staff
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Warehouse</label>
        <select
          value={selectedWarehouse}
          onChange={(e) => setSelectedWarehouse(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Warehouses</option>
          {warehouses.map(w => (
            <option key={w.id} value={w.id}>{w.code} - {w.name}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {activeTab === 'assignments' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : assignments.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="mx-auto text-gray-400 mb-3" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Staff Assigned</h3>
              <p className="text-gray-600 mb-4">
                Assign warehouse managers to start managing inventory
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus size={20} />
                Assign First Staff
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Warehouse
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Staff
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {assignments.map(assignment => (
                  <tr key={assignment.assignment_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {assignment.warehouse_code}
                          </div>
                          <div className="text-xs text-gray-500">
                            {assignment.warehouse_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {assignment.staff_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {assignment.employee_id}
                        </div>
                        <div className="text-xs text-gray-500">
                          {assignment.login_email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {assignment.role_name}
                        </span>
                        {assignment.is_primary_manager && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <UserCheck size={12} />
                            Primary
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {assignment.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle size={12} />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <XCircle size={12} />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(assignment.assigned_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleRemoveAssignment(assignment.assignment_id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Activity size={24} className="text-orange-600" />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Manager Activity Log</h2>
                  <p className="text-sm text-gray-500">All warehouse manager actions and inventory changes</p>
                </div>
              </div>
              <button
                onClick={fetchAllActivities}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>

          {activityLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-gray-400 text-sm">Loading activities...</p>
            </div>
          ) : activities.length === 0 ? (
            <div className="p-12 text-center">
              <Activity size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No activity recorded yet</p>
              <p className="text-sm text-gray-400 mt-1">Manager actions will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {activities.map((activity, idx) => (
                <div key={`${activity.id}-${idx}`} className="p-5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Activity size={18} className="text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                          {activity.action_type?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{activity.staff_name}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-sm text-gray-600">{activity.warehouse_code}</span>
                      </div>
                      {activity.action_description && (
                        <p className="text-sm text-gray-700 mb-1">{activity.action_description}</p>
                      )}
                      {activity.quantity_affected != null && (
                        <p className="text-xs text-gray-500">
                          Quantity: <span className={activity.quantity_affected > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                            {activity.quantity_affected > 0 ? '+' : ''}{activity.quantity_affected}
                          </span>
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(activity.performed_at).toLocaleString('en-IN', { 
                          day: '2-digit', 
                          month: 'short', 
                          year: 'numeric',
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WarehouseManagersPage;
